// Parent view of a child's attendance per course. Defaults to the CURRENT
// semester of the current academic year, computed live from the semester dates
// configured by the administration (Paramètres du Calendrier).
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, ChevronDown, ChevronRight, Clock, Loader2, User, XCircle } from 'lucide-react';
import { ChildSelector } from '../components/ChildSelector';
import { fetchChildClasses, fetchChildModuleAttendance } from '../services/parentService';
import { fetchAcademicYears, type AcademicYear } from '@/services/academicPlanningService';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { getDayName } from '@/types/professor.types';

interface ChildClassInfo {
  moduleElementId: string;
  moduleElementName: string;
  moduleCode: string;
  moduleName: string;
  professorName: string;
  semester: number;
  campusName?: string;
  attendanceRate: number;
  scheduleInfo: { dayOfWeek: number; startTime: string; endTime: string }[];
}

// Current semester from the administration's academic-year parameters; when
// today falls outside both windows, pick the nearest one
const getCurrentSemester = (year: AcademicYear): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const s1Start = year.semester1StartDate ? new Date(year.semester1StartDate) : new Date(year.startDate);
  const s1End = year.semester1EndDate ? new Date(year.semester1EndDate) : null;
  const s2Start = year.semester2StartDate ? new Date(year.semester2StartDate) : null;
  const s2End = year.semester2EndDate ? new Date(year.semester2EndDate) : new Date(year.endDate);
  if (s1End && today >= s1Start && today <= s1End) return 1;
  if (s2Start && s2End && today >= s2Start && today <= s2End) return 2;
  if (today > s2End) return 2;
  if (s1End && today > s1End) return 2;
  return 1;
};

const attendanceColor = (rate: number): string => {
  if (rate >= 80) return 'text-green-600 bg-green-50';
  if (rate >= 60) return 'text-amber-600 bg-amber-50';
  return 'text-red-600 bg-red-50';
};

const STATUS_BADGES: Record<string, { label: string; cls: string }> = {
  PRESENT: { label: 'Présent(e)', cls: 'bg-green-50 text-green-700' },
  LATE: { label: 'Retard', cls: 'bg-amber-50 text-amber-700' },
  ABSENT_EXCUSED: { label: 'Absence justifiée', cls: 'bg-blue-50 text-blue-700' },
  ABSENT_UNEXCUSED: { label: 'Absent(e)', cls: 'bg-red-50 text-red-700' },
  NOT_MARKED: { label: 'Non marqué', cls: 'bg-gray-100 text-gray-500' },
};

interface AttendanceSessionRecord {
  date?: string; // StudentAttendanceHistory records use `date`
  sessionDate?: string;
  status: string;
  lateMinutes?: number | null;
  justification?: string | null;
}

const recordDate = (rec: AttendanceSessionRecord): string => rec.date || rec.sessionDate || '';

// Expanded detail: session-by-session attendance for one course of the child
const CourseAttendanceDetail: React.FC<{ childId: string; moduleElementId: string }> = ({ childId, moduleElementId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['parent-child-module-attendance', childId, moduleElementId],
    queryFn: () => fetchChildModuleAttendance(childId, moduleElementId),
  });

  const records: AttendanceSessionRecord[] = useMemo(() => {
    const d = data as { records?: AttendanceSessionRecord[]; sessions?: AttendanceSessionRecord[] } | AttendanceSessionRecord[] | undefined;
    if (Array.isArray(d)) return d;
    return d?.records || d?.sessions || [];
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4 bg-gray-50">
        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
      </div>
    );
  }
  if (records.length === 0) {
    return <p className="px-6 py-3 text-sm text-gray-500 bg-gray-50">Aucune séance enregistrée</p>;
  }
  return (
    <div className="bg-gray-50 px-6 py-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5">
        {records
          .slice()
          .sort((a, b) => new Date(recordDate(b)).getTime() - new Date(recordDate(a)).getTime())
          .map((rec, i) => {
            const badge = STATUS_BADGES[rec.status] || STATUS_BADGES.NOT_MARKED;
            const d = new Date(recordDate(rec));
            return (
              <div key={i} className="flex items-center justify-between text-sm py-1">
                <span className="text-gray-600">
                  {isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
                <span className="flex items-center gap-2">
                  {rec.justification && (
                    <span className="text-xs text-gray-400 italic truncate max-w-[140px]">{rec.justification}</span>
                  )}
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.cls}`}>
                    {badge.label}
                    {rec.status === 'LATE' && rec.lateMinutes ? ` (${rec.lateMinutes} min)` : ''}
                  </span>
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
};

const ParentAttendancePage: React.FC = () => {
  const { tokens } = useAuthStore();
  const [childId, setChildId] = useState<string | null>(null);
  const [currentYear, setCurrentYear] = useState<AcademicYear | null>(null);
  const [selectedSemester, setSelectedSemester] = useState<number | 'all'>('all');
  const [expandedCourse, setExpandedCourse] = useState<string | null>(null);

  // Load the current academic year and derive the live current semester
  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!tokens?.accessToken) return;
      try {
        const years = await fetchAcademicYears(tokens.accessToken);
        const current = years.find((y: AcademicYear) => y.isCurrent);
        if (current) {
          setCurrentYear(current);
          setSelectedSemester(getCurrentSemester(current));
        }
      } catch {
        // keep defaults — the query below still works without a year filter
      }
    };
    load();
  }, [tokens?.accessToken]);

  const { data, isLoading } = useQuery({
    queryKey: ['parent-child-classes', childId, currentYear?.id],
    queryFn: () => fetchChildClasses(childId as string, currentYear?.id) as Promise<ChildClassInfo[]>,
    enabled: !!childId,
  });

  const classes = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const semesterLabels = useMemo(() => {
    const abs = [...new Set(classes.map((c) => c.semester).filter(Boolean))].sort((a, b) => a - b);
    const odd = abs.find((s) => s % 2 === 1);
    const even = abs.find((s) => s % 2 === 0);
    return { 1: odd ? `Semestre ${odd}` : 'Semestre 1', 2: even ? `Semestre ${even}` : 'Semestre 2' } as Record<number, string>;
  }, [classes]);

  const filtered = useMemo(
    () =>
      classes.filter((c) => {
        if (selectedSemester === 'all' || !c.semester) return true;
        return ((c.semester - 1) % 2) + 1 === selectedSemester;
      }),
    [classes, selectedSemester],
  );

  const avgRate = useMemo(
    () => (filtered.length ? Math.round(filtered.reduce((s, c) => s + (c.attendanceRate || 0), 0) / filtered.length) : null),
    [filtered],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-6 pt-6">
        <ChildSelector onChildChange={setChildId} />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Présences</h1>
            <p className="mt-1 text-sm text-gray-500">
              Taux de présence par cours{currentYear ? ` — ${currentYear.name}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {avgRate !== null && (
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg ${attendanceColor(avgRate)}`}>
                <CheckCircle className="h-4 w-4" />
                {avgRate}% en moyenne
              </span>
            )}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              {([1, 2] as const).map((sem) => (
                <button
                  key={sem}
                  onClick={() => setSelectedSemester(sem)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    selectedSemester === sem ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {semesterLabels[sem]}
                </button>
              ))}
              <button
                onClick={() => setSelectedSemester('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectedSemester === 'all' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Tous
              </button>
            </div>
          </div>
        </div>

        {!childId ? (
          <p className="text-center text-gray-500 py-12">Sélectionnez un enfant pour voir ses présences</p>
        ) : isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <XCircle className="mx-auto h-10 w-10 text-gray-400 mb-2" />
            <p className="text-gray-500">Aucun cours pour cette période</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {filtered
              .slice()
              .sort((a, b) => (a.semester || 0) - (b.semester || 0) || a.moduleElementName.localeCompare(b.moduleElementName, 'fr'))
              .map((cls) => (
                <div key={cls.moduleElementId}>
                  <div
                    onClick={() => setExpandedCourse(expandedCourse === cls.moduleElementId ? null : cls.moduleElementId)}
                    className="px-6 py-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {expandedCourse === cls.moduleElementId ? (
                          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        )}
                        <p className="font-medium text-gray-900 truncate">{cls.moduleElementName}</p>
                        <span className="inline-flex items-center px-2 py-0.5 bg-purple-50 text-purple-700 text-xs font-medium rounded-full flex-shrink-0">
                          S{cls.semester}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 mt-1 ml-6 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5" />
                          {cls.professorName}
                        </span>
                        {cls.scheduleInfo?.[0] && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {getDayName(cls.scheduleInfo[0].dayOfWeek)} {cls.scheduleInfo[0].startTime}-{cls.scheduleInfo[0].endTime}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg flex-shrink-0 ${attendanceColor(cls.attendanceRate || 0)}`}>
                      <CheckCircle className="h-4 w-4" />
                      {cls.attendanceRate ?? 0}%
                    </span>
                  </div>
                  {expandedCourse === cls.moduleElementId && childId && (
                    <CourseAttendanceDetail childId={childId} moduleElementId={cls.moduleElementId} />
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentAttendancePage;
